/**
 * Ключи идемпотентности для mutating-запросов.
 *
 * Ключ должен пережить перезагрузку вкладки: сценарий, ради которого он и нужен,
 * это потерянный ответ после успешного commit, а пользователь в таком случае
 * часто просто перезагружает страницу. Ключ, живущий только в памяти компонента,
 * при этом теряется, следующая отправка уходит с новым ключом и создаёт дубль.
 *
 * Хранилища два. sessionStorage переживает перезагрузку, но недоступен в приватном
 * режиме, при переполненной квоте и под жёсткими политиками cookie. Поэтому поверх
 * него лежит слой в памяти модуля: если запись не удалась, ключ всё равно остаётся
 * стабильным в пределах текущей загрузки страницы, то есть не хуже, чем было.
 */
const STORAGE_PREFIX = 'idempotency:'

interface StoredAttempt {
  key: string
  payload: string
}

const memoryAttempts = new Map<string, StoredAttempt>()

/**
 * `crypto.randomUUID()` доступен только в secure context. По plain HTTP на
 * не-localhost хосте его нет вовсе, и безусловный вызов роняет всю форму вместо
 * того, чтобы деградировать. Fallback не обязан быть криптостойким: ключ живёт
 * минуты и уникален в пределах одного пользователя.
 */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }

  // Последняя линия обороны, когда crypto отсутствует целиком. Длину каждого куска
  // фиксируем: `Math.random().toString(16).slice(2)` иногда возвращает один символ,
  // и такие ключи начинают совпадать между собой.
  return `${Date.now().toString(16)}-${randomHexChunk()}-${randomHexChunk()}`
}

function randomHexChunk(): string {
  return Math.floor(Math.random() * 0x1_0000_0000)
    .toString(16)
    .padStart(8, '0')
}

function readStored(scope: string): StoredAttempt | null {
  const remembered = memoryAttempts.get(scope)
  if (remembered !== undefined) {
    return remembered
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + scope)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as Partial<StoredAttempt>
    if (typeof parsed.key !== 'string' || typeof parsed.payload !== 'string') return null
    return { key: parsed.key, payload: parsed.payload }
  } catch {
    return null
  }
}

function writeStored(scope: string, attempt: StoredAttempt): void {
  memoryAttempts.set(scope, attempt)
  try {
    sessionStorage.setItem(STORAGE_PREFIX + scope, JSON.stringify(attempt))
  } catch {
    // Хранилище недоступно: ключ остаётся стабильным только в памяти страницы.
  }
}

/**
 * Возвращает ключ текущей попытки. Пока payload не изменился, ключ тот же -
 * включая случай, когда страница была перезагружена между попытками. Изменение
 * состава заказа или реквизитов начинает новую попытку с новым ключом.
 */
export function idempotencyKeyForAttempt(scope: string, payload: string): string {
  const stored = readStored(scope)
  if (stored !== null && stored.payload === payload) {
    return stored.key
  }

  const attempt: StoredAttempt = { key: generateIdempotencyKey(), payload }
  writeStored(scope, attempt)

  return attempt.key
}

/** Вызывается после подтверждённого результата: следующая отправка - новая попытка. */
export function clearIdempotencyAttempt(scope: string): void {
  memoryAttempts.delete(scope)
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + scope)
  } catch {
    // См. writeStored.
  }
}
